/**
 * @file   : ContactUs.js
 * @author : Amarjeet Lamba (Amarjeet.Lamba@AspiraConnect.com)
 * @date   : 1/29/2019, 12:18:30 PM
 */
import PropTypes from 'prop-types'
import React from 'react'

import SimpleAnimate, {
  CMP_TYPE_DIV,
} from '../../external/components/animation/SimpleAnimate'
import BoxContainer from '../../external/components/container/BoxContainer'
import DynamicGrid from '../../external/components/container/DynamicGrid'
import HighlightContainer, {
  HIGHLIGHT_IMPORTANT,
} from '../../external/components/container/HighlightContainer'
import SimpleContainer from '../../external/components/container/SimpleContainer'
import FlipCard from '../../external/components/container/flipCard/FlipCard'
import TypeaheadCmp from '../../external/components/form/typeahead/TypeaheadCmp'
import ACol from '../../external/components/layout/ACol'
import AGrid from '../../external/components/layout/AGrid'
import ARow from '../../external/components/layout/ARow'
import Cont from '../../external/components/layout/Cont'
import Section from '../../external/components/layout/Section'
import ABaseLink from '../../external/components/link/ABaseLink'
import AIconAction from '../../external/components/link/AIconAction'
import TextHoverable, {
  BACKGROUND_COLOR,
} from '../../external/components/link/TextHoverable'
import SimpleModal from '../../external/components/overlay/modal/SimpleModal'
import HTag from '../../external/components/text/HTag'
import TextBlock from '../../external/components/text/TextBlock'
import TextStyler, {
  DARK_BLUE_TEXT,
  FONT_WEIGHT,
} from '../../external/components/text/TextStyler'
import { isEmpty } from '../../external/utilities/GeneralUtils'
import { ROLES } from '../../external/utilities/accessibility/Definitions'
import { getContactUsRoute } from '../../router/ExtRouteManager'
import { getAdConfig } from '../../utilities/ConfigUtils'
import { simpleTranslate, translate } from '../Translation'
import AdvertWrapped from '../shared/ad/AdvertWrapped'
import SimpleHero from '../shared/header/SimpleHero'
import HackMarketingContentWrapper from '../sharedInternal/container/HackMarketingContentWrapper'
import Padder, { PAD_10 } from '../sharedInternal/container/Padder'
import PageBase from '../sharedInternal/container/page/PageBase'
import PageContent from '../sharedInternal/container/page/PageContent'
import ContactUsCardBack from './cards/ContactUsCardBack'
import ContactUsCardFront from './cards/ContactUsCardFront'

const ContactUsLayout = ({
  theme,
  intl,
  selectedClients = [],
  searchCritera = [],
  clientSearchResults = [],
  selectedSearchTerm = '',
  onSearchTrigger,
  onSearchTermChange,
  onSearchResultItemSelectRequest,
  onClearSearchRequest,
  mainConfig,
  contactModalOpen,
  selectedClient,
  selectedClientChannel,
  onSelectClientChannelRequest,
  onContactModalCloseRequest,
}) => {
  const getFlipCard = function (client) {
    if (isEmpty(client)) {
      return <Cont></Cont>
    }
    const cardSize = '2x'
    const isShowCardBack = true
    const cardContentFront = <ContactUsCardFront client={client} />
    const cardLabelBtnFront = translate(intl, 'label_flip_card_front_btn', {
      description: client.name,
    })
    const cardLabelBtnBack = translate(intl, 'label_flip_card_back_btn', {
      description: client.name,
    })

    let cardContentBack = null
    if (isShowCardBack) {
      cardContentBack = (
        <ContactUsCardBack
          intl={intl}
          client={client}
          onSelectClientChannelRequest={onSelectClientChannelRequest}
        />
      )
    }

    return (
      <SimpleAnimate
        transitionLeave={false}
        transitionEnter={true}
        transitionAppear={true}
        componentType={CMP_TYPE_DIV}
      >
        <FlipCard
          key={client.name}
          isShowMoreButton={isShowCardBack}
          cardSize={cardSize}
          cardContentFront={cardContentFront}
          cardContentBack={cardContentBack}
          cardLabelBtnFront={cardLabelBtnFront}
          cardLabelBtnBack={cardLabelBtnBack}
        />
      </SimpleAnimate>
    )
  }

  const getLabelValueStyledText = function (label = '', value = '') {
    return (
      <Cont asSpan>
        <TextStyler
          styleObj={{
            ...DARK_BLUE_TEXT,
            fontWeight: FONT_WEIGHT.BOLD,
          }}
        >
          {label}
        </TextStyler>
        <TextStyler styleObj={{ ...DARK_BLUE_TEXT }}>{value}</TextStyler>
      </Cont>
    )
  }

  const getHeaderUI = function () {
    const headerUI = (
      <Cont>
        <HTag h1>
          <TextHoverable
            asSpan
            fontSizeType={2}
            fontFamily={'Georgia'}
            fontColor={'white'}
            backgroundColor={BACKGROUND_COLOR.BLUE80}
            selected
          >
            {simpleTranslate(intl, 'contact_us_page_title')}
          </TextHoverable>
        </HTag>
      </Cont>
    )

    return (
      <SimpleHero
        headerContent={headerUI}
        imageClass={'contact-us-page-hero'}
      ></SimpleHero>
    )
  }

  const getClientsUI = function () {
    const searchCriteriaNames = []
    if (!isEmpty(searchCritera)) {
      for (const searchResult of searchCritera) {
        searchCriteriaNames.push(searchResult.name)
      }
    }
    let currSearchUI = null
    let clientsUI = null
    let searchUI = null

    searchUI = (
      <AGrid fluid>
        <ARow>
          <ACol xs={12} sm={6} md={6} lg={6}></ACol>
          <ACol xs={12} sm={6} md={6} lg={6}>
            <TypeaheadCmp
              showSearchTrigger
              onSearchTrigger={onSearchTrigger}
              onSearchTermChange={onSearchTermChange}
              allQuickSearchResults={clientSearchResults}
              onSearchResultItemSelectRequest={onSearchResultItemSelectRequest}
              resetOnSelect={false}
              placeHolderMsg={simpleTranslate(
                intl,
                'contact_us_search_placeholder',
              )}
            />
          </ACol>
        </ARow>
      </AGrid>
    )

    if (!isEmpty(selectedSearchTerm)) {
      currSearchUI = (
        <SimpleAnimate componentType={CMP_TYPE_DIV}>
          <Padder padBottom={PAD_10}>
            <HighlightContainer style={HIGHLIGHT_IMPORTANT}>
              <Cont cmpRole={ROLES.WIDGET.ALERT}>
                <Cont>
                  <AIconAction
                    onClick={onClearSearchRequest}
                    isShowAsLink
                    asSpan
                    faStyle={'fa fa-remove'}
                    cmpLabel={simpleTranslate(intl, 'label_clear_search')}
                  />
                  {getLabelValueStyledText(
                    simpleTranslate(intl, 'label_search_results'),
                    `"${selectedSearchTerm}" `,
                  )}
                </Cont>
                <Cont>
                  {isEmpty(searchCriteriaNames)
                    ? null
                    : getLabelValueStyledText(
                        translate(intl, 'label_search_results_matches'),
                        searchCriteriaNames.join(', '),
                      )}
                </Cont>
                <Cont>
                  {isEmpty(selectedClients)
                    ? translate(intl, 'label_search_results_empty')
                    : null}
                </Cont>
              </Cont>
            </HighlightContainer>
          </Padder>
        </SimpleAnimate>
      )
    }
    if (!isEmpty(selectedClients)) {
      clientsUI = (
        <DynamicGrid
          items={selectedClients}
          getCellContent={getFlipCard}
          fluid
        />
      )
    }

    return (
      <SimpleContainer>
        <Cont>
          {searchUI}
          {currSearchUI}
          {clientsUI}
        </Cont>
      </SimpleContainer>
    )
  }

  const getDescriptionUI = function () {
    return (
      <BoxContainer borderColor={'grey-medium'}>
        <Cont>
          <TextBlock
            isHTML
            content={simpleTranslate(intl, 'contact_us_note1')}
          />
          <br/>
          <TextBlock
            isHTML
            content={simpleTranslate(intl, 'contact_us_note2')}
          />
        </Cont>
      </BoxContainer>
    )
  }

  const getFootnoteUI = function () {
    return (
      <BoxContainer borderColor={'grey-medium'}>
        <Cont>
          <Cont asSpan>{simpleTranslate(intl, 'contact_us_alert1')}</Cont>
          <ABaseLink to={getContactUsRoute()} internal={false} newWin={true}>
            {simpleTranslate(intl, 'contact_us_alert2')}
          </ABaseLink>
        </Cont>
        <br/>
        <i>{simpleTranslate(intl, 'contact_us_title')}</i>

        <div>
          <br/>
          <b>{simpleTranslate(intl, 'contact_us_email1_title')}</b>
          <p>{simpleTranslate(intl, 'contact_us_email1_subtitle')}</p>
          <Cont >
            <Cont asSpan>{simpleTranslate(intl, 'contact_us_email_at')}</Cont>
            <Cont asSpan className="emphasis underline">Sales@AspiraConnect.com</Cont>
          </Cont>
        </div>

        <div>
          <br/>
          <b>{simpleTranslate(intl, 'contact_us_email2_title')}</b>
          <p>{simpleTranslate(intl, 'contact_us_email2_subtitle')}</p>
          <Cont >
            <Cont asSpan>{simpleTranslate(intl, 'contact_us_email_at')}</Cont>
            <Cont asSpan className="emphasis underline">PR@AspiraConnect.com</Cont>
          </Cont>
        </div>
        
      </BoxContainer>
    )
  }

  const getFooterAd = function () {
    if (isEmpty(mainConfig)) {
      return null
    }

    return <AdvertWrapped id={'HP6'} adConfig={getAdConfig(mainConfig)} />
  }

  const getContactModal = function () {
    return (
      <SimpleModal
        show={contactModalOpen}
        closeButton
        header={translate(intl, 'contact_client', {
          clientName: selectedClient.name,
        })}
        body={
          <Cont>
            {translate(
              intl,
              `contact_client_channel_${selectedClientChannel.type}`,
              {
                clientName: selectedClient.name,
                identifier: selectedClientChannel.identifier,
              },
            )}
          </Cont>
        }
        onHide={onContactModalCloseRequest}
      />
    )
  }

  return (
    <PageBase showGenericBG>
      {getHeaderUI()}
      <PageContent noHeader>
        {/* <Section>
          <PageTitle title={simpleTranslate(intl, 'contact_us_page_title')} />
        </Section> */}
        <Section>{getDescriptionUI()}</Section>
        <HackMarketingContentWrapper>
          {getClientsUI()}
        </HackMarketingContentWrapper>
        <Section />
        {getFootnoteUI()}
      </PageContent>
      <Section />
      {getContactModal()}
      {getFooterAd()}
    </PageBase>
  )
}

ContactUsLayout.propTypes = {
  theme: PropTypes.string.isRequired,
  intl: PropTypes.object.isRequired,
  selectedClients: PropTypes.array.isRequired,
  searchCritera: PropTypes.array.isRequired,
  clientSearchResults: PropTypes.array.isRequired,
  selectedSearchTerm: PropTypes.string.isRequired,
  onSearchTrigger: PropTypes.func.isRequired,
  onSearchTermChange: PropTypes.func.isRequired,
  onSearchResultItemSelectRequest: PropTypes.func.isRequired,
  onClearSearchRequest: PropTypes.func.isRequired,
  mainConfig: PropTypes.object.isRequired,
  contactModalOpen: PropTypes.bool.isRequired,
  selectedClient: PropTypes.object,
  selectedClientChannel: PropTypes.object,
  onSelectClientChannelRequest: PropTypes.func.isRequired,
  onContactModalCloseRequest: PropTypes.func.isRequired,
}

export default ContactUsLayout
